/**
 * HealingEntry — Premium entry screen for healing experiences.
 *
 * Layered with the universal AudioEntry: the "Begin" CTA is now
 * the 3-button Clear / Soft / Deep selector. Tapping a mode calls
 * onBegin(mode) so each pillar can configure its audio engine.
 *
 * Soft nature background + breathing scale + warm ambient glow are
 * preserved exactly as before.
 */

import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import AudioEntry from '../../components/audio/AudioEntry';

const BG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/85b307c0-58ce-40d0-8126-3abffae3b4a9/images/7b10f84fad84915141fbfafafafb56cfb9843f832f236ae9079caa5d5c99a51a.png';

const HealingEntry = ({
  title,
  subtitle,
  description,
  onBegin,
  onBack,
  area,                  // e.g. "healing_spinal" — used for "Remember my preference"
  defaultMode = 'soft',
  glow = 'rgba(160,180,140,0.18)',  // pillar-specific accent (subtle radial behind buttons)
  accent = 'rgba(160,180,140,0.35)',// pillar-specific divider tint
}) => {
  return (
    <motion.div
      className="fixed inset-0 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2.5, ease: 'easeInOut' }}
      data-testid="healing-entry"
    >
      {/* Background — soft nature with slow breathing scale */}
      <motion.div
        className="fixed inset-0"
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.55) saturate(0.75)' }} />
      </motion.div>

      {/* Gradient overlay */}
      <div className="fixed inset-0" style={{
        background: 'linear-gradient(180deg, rgba(8,12,8,0.05) 0%, rgba(8,12,8,0.15) 25%, rgba(8,12,8,0.55) 55%, rgba(8,12,8,0.92) 100%)',
      }} />

      {/* Pillar-specific accent glow — softly breathes behind the AudioEntry */}
      <motion.div
        className="fixed inset-0 pointer-events-none"
        animate={{
          opacity: [0.65, 1, 0.65],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          background: `radial-gradient(ellipse 95% 70% at 50% 62%, ${glow} 0%, transparent 70%)`,
          mixBlendMode: 'screen',
        }}
      />

      {/* Warm ambient glow */}
      <motion.div
        className="fixed inset-0 pointer-events-none"
        animate={{
          background: [
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(160,180,140,0.04) 0%, transparent 60%)',
            'radial-gradient(ellipse 90% 70% at 52% 42%, rgba(140,170,130,0.06) 0%, transparent 60%)',
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(160,180,140,0.04) 0%, transparent 60%)',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Back button */}
      <div className="relative z-10 px-6 pt-6">
        <motion.button
          onClick={onBack}
          className="p-2.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}
          whileTap={{ scale: 0.92 }}
          data-testid="healing-back-btn"
        >
          <ArrowLeft size={18} className="text-white/40" />
        </motion.button>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        {/* Title */}
        <motion.h1
          className="text-3xl sm:text-4xl text-white/85 text-center mb-3"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', letterSpacing: '0.02em' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
        >
          {title}
        </motion.h1>

        {/* Subtle divider — pillar accent */}
        <motion.div
          className="w-12 h-px mb-5"
          style={{ background: accent }}
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        />

        {/* Subtitle */}
        <motion.p
          className="text-white/45 text-sm mb-3 text-center"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {subtitle}
        </motion.p>

        {/* Description */}
        {description && (
          <motion.p
            className="text-white/30 text-xs text-center max-w-sm leading-relaxed mb-8"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {description}
          </motion.p>
        )}

        {/* Universal Audio Entry — 3 modes */}
        <motion.div
          className="w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <AudioEntry
            defaultMode={defaultMode}
            area={area}
            onSelect={(mode) => onBegin?.(mode)}
          />
        </motion.div>
      </div>

      {/* Disclaimer footer */}
      <motion.div
        className="relative z-10 px-8 pb-6 pt-2 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <p
          className="text-white/15 text-[10px] leading-relaxed max-w-sm mx-auto"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="healing-disclaimer"
        >
          This experience supports relaxation alongside your medical care.
          It is not a substitute for professional medical advice, diagnosis, or treatment.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default HealingEntry;
